import {
    Box,
    Tooltip as MuiTooltip,
    Popover,
    TooltipProps,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useCallback, useId, useState } from "react";

interface ITooltipProps extends TooltipProps {
    title: string;
    description?: string;
    icon?: React.ReactNode;
    color?: string;
    children: React.ReactElement;
}

const StyledTooltip = styled(
    ({ className, ...props }: React.ComponentProps<typeof MuiTooltip>) => (
        <MuiTooltip {...props} classes={{ popper: className }} />
    )
)(({ theme }) => ({
    "& .MuiTooltip-tooltip": {
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        boxShadow: theme.shadows[3],
        borderRadius: 8,
        padding: 0,
        maxWidth: 260,
        border: `1px solid ${theme.palette.divider}`,
    },
    "& .MuiTooltip-arrow": {
        color: theme.palette.background.paper,
        "&::before": {
            border: `1px solid ${theme.palette.divider}`,
        },
    },
}));

const TooltipContent = styled(Box)(({ theme }) => ({
    padding: theme.spacing(1.5, 2),
}));

const TooltipHeader = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
}));

const TooltipTitle = styled(Typography)({
    fontSize: "0.8125rem",
    fontWeight: 600,
});

const TooltipDescription = styled(Typography)(({ theme }) => ({
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    lineHeight: 1.4,
    marginTop: theme.spacing(0.5),
}));

const PopoverContent = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2),
    maxWidth: 280,
    [theme.breakpoints.down("sm")]: {
        padding: theme.spacing(2.5),
        maxWidth: "90vw",
    },
}));

const PopoverTitle = styled(Typography)(({ theme }) => ({
    fontSize: "0.875rem",
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(0.5),
}));

const PopoverDescription = styled(Typography)(({ theme }) => ({
    fontSize: "0.8125rem",
    color: theme.palette.text.secondary,
    lineHeight: 1.5,
    mt: 1,
}));

export const Tooltip = (props: ITooltipProps) => {
    const { title, description, icon, color, children, ...rest } = props;

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const popoverId = useId();

    const handleClick = useCallback(
        (event: React.MouseEvent<HTMLElement>) => {
            if (isMobile) {
                setAnchorEl(event.currentTarget);
            }
        },
        [isMobile]
    );

    const handleClose = useCallback(() => {
        setAnchorEl(null);
    }, []);

    if (!title && !description) return children;

    const isPopoverOpen = Boolean(anchorEl);
    const isControlled = "open" in rest;

    if (isMobile && !isControlled) {
        return (
            <>
                <Box onClick={handleClick} sx={{ display: "inline-flex" }}>
                    {children}
                </Box>
                <Popover
                    id={popoverId}
                    open={isPopoverOpen}
                    anchorEl={anchorEl}
                    onClose={handleClose}
                    anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "center",
                    }}
                    transformOrigin={{
                        vertical: "top",
                        horizontal: "center",
                    }}
                    slotProps={{
                        paper: {
                            sx: {
                                borderRadius: 2,
                                boxShadow: theme.shadows[3],
                                mt: 1,
                            },
                        },
                    }}
                >
                    <PopoverContent>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                            }}
                        >
                            {icon && (
                                <Box sx={{ color, display: "flex" }}>
                                    {icon}
                                </Box>
                            )}
                            <PopoverTitle style={color ? { color } : undefined}>
                                {title}
                            </PopoverTitle>
                        </Box>
                        {description && (
                            <PopoverDescription>
                                {description}
                            </PopoverDescription>
                        )}
                    </PopoverContent>
                </Popover>
            </>
        );
    }

    return (
        <StyledTooltip
            title={
                <TooltipContent>
                    <TooltipHeader>
                        {icon && (
                            <Box sx={{ color, display: "flex" }}>{icon}</Box>
                        )}
                        <TooltipTitle style={color ? { color } : undefined}>
                            {title}
                        </TooltipTitle>
                    </TooltipHeader>
                    {description && (
                        <TooltipDescription>{description}</TooltipDescription>
                    )}
                </TooltipContent>
            }
            arrow
            placement="bottom"
            enterDelay={200}
            leaveDelay={100}
            {...rest}
        >
            {children}
        </StyledTooltip>
    );
};
